import {
  BillingPaymentNotePayload,
  PaymentEntry,
  PaymentType,
} from '@dataTypes/billing.types'
import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_BILLING', '')

export type StripePaymentStatus =
  | 'pending'
  | 'processing'
  | 'requires_action'
  | 'succeeded'
  | 'failed'
  | 'refunded'

export interface StripePaymentRecord {
  stripe_payment_id: string
  payment_id: string
  idempotency_key: string
  payment_method_id?: string | null
  invoice_ids: string[]
  stripe_charge_id?: string | null
  stripe_intent_id: string
  status: StripePaymentStatus
  failure_reason?: string | null
}

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export type PaymentQueryFilters = {
  patient_id?: string
  clinic_id?: string
  payment_type?: PaymentType | PaymentType[]
  /** Case-insensitive search on payer first/last name (backend query object). */
  search?: string
  limit?: number
  offset?: number
  /** sort: `paymentDate|method|payer` + `:ASC|:DESC` (e.g. `paymentDate:DESC`). */
  sort?: string
}

export type CreateCashPaymentEntryRequest = {
  amount: number
  invoice_ids: string[]
  paid_by: string
  created_by: string
  clinic_id: string
  reference_number?: string
  notes?: BillingPaymentNotePayload[]
}

export type CreateCardPaymentEntryRequest = {
  amount: number
  invoice_ids: string[]
  paid_by: string
  created_by: string
  clinic_id: string
  payment_method_id: string
  idempotency_key: string
  reference_number?: string
  notes?: BillingPaymentNotePayload[]
}

export type CreateCardPaymentEntryResponse = ApiResponse<{
  payment?: unknown
  stripe_payment?: StripePaymentRecord
  client_secret?: string | null
  requires_action?: boolean
}>

export type GetStripePaymentStatusResponse = ApiResponse<{
  stripe_payment?: StripePaymentRecord
}>

export type GetPaymentEntriesResponse = ApiResponse<{
  entries?: PaymentEntry[]
  count?: number
}>

export type CreateCashPaymentEntryResponse = ApiResponse<{
  payment?: unknown
  invoice_payments?: unknown[]
  appointment_payments?: unknown[]
}>

export const getPaymentEntries = async (
  filters?: PaymentQueryFilters,
): Promise<GetPaymentEntriesResponse> => {
  try {
    const response = await axios.get<GetPaymentEntriesResponse>(
      `${BUSINESS_LAYER_API}/api/v1/payment`,
      {
        params: filters,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getPaymentEntries:', e)
    throw e
  }
}

export type ExportPaymentCsvFilters = Partial<PaymentQueryFilters>

export const exportPaymentEntriesCsv = async (
  filters?: ExportPaymentCsvFilters,
): Promise<Blob> => {
  const response = await axios.get<Blob>(
    `${BUSINESS_LAYER_API}/api/v1/payment/export/csv`,
    { params: filters ?? {}, responseType: 'blob' },
  )
  return response.data
}

export const createCashPaymentEntry = async (
  payload: CreateCashPaymentEntryRequest,
): Promise<CreateCashPaymentEntryResponse> => {
  try {
    const response = await axios.post<CreateCashPaymentEntryResponse>(
      `${BUSINESS_LAYER_API}/api/v1/payment/cash`,
      payload,
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in createCashPaymentEntry:', e)
    throw e
  }
}

export const createCardPaymentEntry = async (
  payload: CreateCardPaymentEntryRequest,
): Promise<CreateCardPaymentEntryResponse> => {
  try {
    const response = await axios.post<CreateCardPaymentEntryResponse>(
      `${BUSINESS_LAYER_API}/api/v1/payment/card`,
      payload,
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in createCardPaymentEntry:', e)
    throw e
  }
}

export const getStripePaymentStatus = async (
  idempotencyKey: string,
): Promise<GetStripePaymentStatusResponse> => {
  try {
    const response = await axios.get<GetStripePaymentStatusResponse>(
      `${BUSINESS_LAYER_API}/api/v1/stripe-payments/status/${encodeURIComponent(idempotencyKey)}`,
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in getStripePaymentStatus:', e)
    throw e
  }
}
